// Sacred Report v5.1 visual assets — crystal gradients, archetypes, yantras,
// Devanagari mantras, category badges, section subtitles. Data only, no logic.

// ────────── Crystal color database (primary + accent per stone) ──────────
export const CRYSTAL_COLORS = {
  "Red Jasper":        { primary: "#C0392B", accent: "#E74C3C" },
  "Black Tourmaline":  { primary: "#1A1A1A", accent: "#3A3A3A" },
  "Smoky Quartz":      { primary: "#5C4A30", accent: "#8B6C42" },
  "Jade":              { primary: "#3D8B5C", accent: "#7FC97F" },
  "Bloodstone":        { primary: "#2D6A4F", accent: "#4D8B5C" },
  "Green Aventurine":  { primary: "#52C476", accent: "#86EFAC" },
  "Emerald":           { primary: "#0E7C44", accent: "#34D399" },
  "Peridot":           { primary: "#7FB069", accent: "#BBF7D0" },
  "Obsidian":          { primary: "#0A0A0A", accent: "#2C2C2C" },
  "Aquamarine":        { primary: "#5BAFC4", accent: "#A5D8E8" },
  "Moonstone":         { primary: "#A8B5C4", accent: "#E0E8F0" },
  "Blue Lace Agate":   { primary: "#6FA0C4", accent: "#A8C8E0" },
  "Rose Quartz":       { primary: "#D4828F", accent: "#F4A7B9" },
  "Pearl":             { primary: "#C9B97E", accent: "#E8E0C8" },
  "Rhodonite":         { primary: "#9B4F61", accent: "#E8A0B0" },
  "Carnelian":         { primary: "#C2410C", accent: "#FB923C" },
  "Pyrite":            { primary: "#92710F", accent: "#FCD34D" },
  "Sunstone":          { primary: "#C2410C", accent: "#FED7AA" },
  "Tiger Eye":         { primary: "#8B5A00", accent: "#F5C842" },
  "Citrine":           { primary: "#B8860B", accent: "#FCD34D" },
  "Garnet":            { primary: "#7B1818", accent: "#B91C1C" },
  "Clear Quartz":      { primary: "#C0C0D0", accent: "#FFFFFF" },
  "Sodalite":          { primary: "#1E3A8A", accent: "#3B82F6" },
  "Lapis Lazuli":      { primary: "#1E3A8A", accent: "#2255A4" },
  "Yellow Topaz":      { primary: "#B8860B", accent: "#FDE68A" },
  "Celestite":         { primary: "#6080A8", accent: "#B0C8E0" },
  "Lepidolite":        { primary: "#9B6FB0", accent: "#D4B5E0" },
  "Turquoise":         { primary: "#0E7C7B", accent: "#5EEAD4" },
  "Amethyst":          { primary: "#6B21A8", accent: "#C084FC" },
  "Selenite":          { primary: "#D0D0D0", accent: "#FFFFFF" },
  "Fluorite":          { primary: "#7C3AED", accent: "#C4B5FD" },
};

export function getCrystalGradient(crystalName) {
  const c = CRYSTAL_COLORS[crystalName] || { primary: "#888888", accent: "#CCCCCC" };
  const darkR = Math.max(0, parseInt(c.primary.slice(1, 3), 16) - 60);
  const darkG = Math.max(0, parseInt(c.primary.slice(3, 5), 16) - 60);
  const darkB = Math.max(0, parseInt(c.primary.slice(5, 7), 16) - 60);
  const dark = "#" + [darkR, darkG, darkB].map((n) => n.toString(16).padStart(2, "0")).join("");
  return `radial-gradient(circle at 30% 30%, ${c.accent} 0%, ${c.primary} 50%, ${dark} 100%)`;
}

export const ORB_SHADOW =
  "inset -3px -3px 8px rgba(0,0,0,0.4), inset 3px 3px 6px rgba(255,255,255,0.3), 0 2px 6px rgba(0,0,0,0.15)";

// ────────── Element-tinted card palette for Pancha Foundation ──────────
export const ELEMENT_PALETTE = {
  earth: { bg: "#DCFCE7", border: "#16A34A", label: "#15803D", sanskrit: "#166534" },
  water: { bg: "#DBEAFE", border: "#2563EB", label: "#1E40AF", sanskrit: "#1E3A8A" },
  fire:  { bg: "#FFEDD5", border: "#EA580C", label: "#C2410C", sanskrit: "#9A3412" },
  air:   { bg: "#FAF5FF", border: "#9333EA", label: "#7E22CE", sanskrit: "#6B21A8" },
  ether: { bg: "#F0EAFF", border: "#7C3AED", label: "#5B21B6", sanskrit: "#4C1D95" },
};

// ────────── Archetype + theme lookups ──────────
export const LP_ARCHETYPE = {
  1: "Sun · The Pioneer", 2: "Moon · The Peacemaker", 3: "Jupiter · The Creator",
  4: "Rahu · The Builder", 5: "Mercury · The Explorer", 6: "Venus · The Nurturer",
  7: "Ketu · The Mystic", 8: "Saturn · The Achiever", 9: "Mars · The Humanitarian",
  11: "Master Moon · The Messenger", 22: "Master Saturn · The Builder",
  33: "Master Jupiter · The Teacher",
};

export const NN_ARCHETYPE = {
  1: "Sun · Self-expression", 2: "Moon · Receptivity", 3: "Jupiter · Joy",
  4: "Rahu · Stability", 5: "Mercury · Communicator", 6: "Venus · Love",
  7: "Ketu · Insight", 8: "Saturn · Authority", 9: "Mars · Service",
  11: "Moon · Intuition", 22: "Saturn · Mastery",
};

export const ZODIAC_ARCHETYPE = {
  Aries: "Mars · The Pioneer", Taurus: "Venus · The Builder",
  Gemini: "Mercury · The Twin", Cancer: "Moon · The Caregiver",
  Leo: "Sun · The Radiant", Virgo: "Mercury · The Healer",
  Libra: "Venus · The Diplomat", Scorpio: "Mars · The Transformer",
  Sagittarius: "Jupiter · The Seeker", Capricorn: "Saturn · The Master",
  Aquarius: "Saturn · The Visionary", Pisces: "Jupiter · The Mystic",
};

export const PY_THEME = {
  1: "New beginnings", 2: "Cooperation", 3: "Creative expansion",
  4: "Foundation building", 5: "Change and freedom", 6: "Love and service",
  7: "Inner reflection", 8: "Material harvest", 9: "Year of completion",
};

// Poetic subtitle phrase based on zodiac element
export function zodiacCelestialPhrase(zodiac) {
  const fire = ["Aries", "Leo", "Sagittarius"];
  const earth = ["Taurus", "Virgo", "Capricorn"];
  const air = ["Gemini", "Libra", "Aquarius"];
  const water = ["Cancer", "Scorpio", "Pisces"];
  if (fire.includes(zodiac)) return `radiant Sun of ${zodiac}`;
  if (earth.includes(zodiac)) return `grounding Earth of ${zodiac}`;
  if (air.includes(zodiac)) return `celestial Air of ${zodiac}`;
  if (water.includes(zodiac)) return `luminous Moon of ${zodiac}`;
  return zodiac;
}

// ────────── Devanagari mantras + petal counts per chakra ──────────
export const CHAKRA_MANTRAS = {
  Muladhara:    { roman: "LAM", devanagari: "\u0932\u0902", petals: 4 },
  Svadhisthana: { roman: "VAM", devanagari: "\u0935\u0902", petals: 6 },
  Manipura:     { roman: "RAM", devanagari: "\u0930\u0902", petals: 10 },
  Anahata:      { roman: "YAM", devanagari: "\u092F\u0902", petals: 12 },
  Vishuddha:    { roman: "HAM", devanagari: "\u0939\u0902", petals: 16 },
};

// ────────── Section dividers + subtitles ──────────
export const SECTION_SUBTITLES = {
  "DOMINANT CHAKRA": "The energy point in your body your bracelet turns on",
  "SACRED BLUEPRINT": "What your birthday and name say about you",
  "PANCHA FOUNDATION": "The five 'element' stones that hold your bracelet together",
  "PRESCRIPTION CRYSTALS": "The stones picked specifically for you",
  "SOUL ESSENCE": "The quiet truth of who you are",
  "LIFE PATH MISSION": "What your life keeps trying to teach you",
  "NAME NUMBER ENERGY": "What people feel the moment they meet you",
  "CORE CHARACTERISTICS": "Your strengths — and where you get in your own way",
  "PERSONAL YEAR": "Where you are on your 9-year loop right now",
  "ENERGY AHEAD": "What next year is bringing your way",
  "KARMA LESSON": "The big life lesson your soul came here to learn",
  "SACRED GIFTS": "The gifts you were born carrying",
  "SPIRITUAL PRACTICES": "Small daily rituals that fit your energy",
  "SACRED GUIDANCE": "What to lean into, and what to skip",
  "SACRED AFFIRMATION": "One line to say out loud every morning",
};

// ────────── Prescription category badge palette ──────────
export const RX_BADGE_COLORS = {
  "LIFE PATH":    { bg: "rgba(106,13,173,0.08)",  color: "#6A0DAD" },
  "NAME NUMBER":  { bg: "rgba(106,13,173,0.08)",  color: "#6A0DAD" },
  "ZODIAC":       { bg: "rgba(106,13,173,0.08)",  color: "#6A0DAD" },
  "WEALTH":       { bg: "rgba(212,168,85,0.15)",  color: "#8B6914" },
  "LUCK":         { bg: "rgba(34,197,94,0.10)",   color: "#15803D" },
  "HEALTH":       { bg: "rgba(220,38,38,0.10)",   color: "#991B1B" },
  "STRESS":       { bg: "rgba(168,85,247,0.10)",  color: "#7E22CE" },
  "LOVE":         { bg: "rgba(244,114,182,0.10)", color: "#BE185D" },
  "CONFIDENCE":   { bg: "rgba(234,88,12,0.10)",   color: "#9A3412" },
  "PROTECTION":   { bg: "rgba(31,41,55,0.10)",    color: "#1F2937" },
  "CLEANSING":    { bg: "rgba(168,85,247,0.10)",  color: "#7E22CE" },
  "WISDOM":       { bg: "rgba(37,99,235,0.10)",   color: "#1E40AF" },
  "PSYCHIC":      { bg: "rgba(124,58,237,0.10)",  color: "#5B21B6" },
  "SPACERS":      { bg: "rgba(106,13,173,0.06)",  color: "#6B5E7E" },
};

// Extract badge key from the `source` string set by the prescription builder.
// e.g. "Life Path 5" → "LIFE PATH"; "Intention: Wealth, Success and Confidence" → "WEALTH".
export function badgeKeyFromSource(source) {
  if (!source) return "LIFE PATH";
  const s = source.toLowerCase();
  if (s.startsWith("life path")) return "LIFE PATH";
  if (s.startsWith("name number")) return "NAME NUMBER";
  if (s.startsWith("zodiac")) return "ZODIAC";
  if (s.startsWith("spacers")) return "SPACERS";
  if (s.includes("wealth")) return "WEALTH";
  if (s.includes("luck") || s.includes("prosperity")) return "LUCK";
  if (s.includes("health") || s.includes("wellbeing")) return "HEALTH";
  if (s.includes("stress")) return "STRESS";
  if (s.includes("love") || s.includes("compassion")) return "LOVE";
  if (s.includes("confidence") || s.includes("courage")) return "CONFIDENCE";
  if (s.includes("protection") || s.includes("grounding")) return "PROTECTION";
  if (s.includes("cleansing") || s.includes("purification")) return "CLEANSING";
  if (s.includes("wisdom") || s.includes("communication")) return "WISDOM";
  if (s.includes("psychic") || s.includes("awakening") || s.includes("intuition")) return "PSYCHIC";
  return "LIFE PATH";
}

// ────────── Chakra yantras — authentic SVG sacred geometry ──────────
// Returned as raw SVG strings so they can be inlined with dangerouslySetInnerHTML.
// SAFETY: every key/value below is an authored constant. There is NO user input,
// NO dynamic interpolation, and NO data from the network or database that flows
// into these strings. Static analysers will still flag the consumers (e.g.
// OrderDetail.jsx) because the API is named `dangerously…`, but the trust
// boundary is documented: hardcoded brand artwork only.
export const CHAKRA_YANTRAS = {
  Muladhara: `<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="40" fill="rgba(255,255,255,0.5)" stroke="#B91C1C" stroke-width="0.5"/>
    <g transform="translate(50,50)">
      <g fill="rgba(185,28,28,0.3)" stroke="#991B1B" stroke-width="1">
        <path d="M 0,-36 Q -10,-26 -8,-18 L 8,-18 Q 10,-26 0,-36 Z"/>
        <path d="M 0,-36 Q -10,-26 -8,-18 L 8,-18 Q 10,-26 0,-36 Z" transform="rotate(90)"/>
        <path d="M 0,-36 Q -10,-26 -8,-18 L 8,-18 Q 10,-26 0,-36 Z" transform="rotate(180)"/>
        <path d="M 0,-36 Q -10,-26 -8,-18 L 8,-18 Q 10,-26 0,-36 Z" transform="rotate(270)"/>
      </g>
      <rect x="-14" y="-14" width="28" height="28" fill="rgba(245,200,66,0.5)" stroke="#A16207" stroke-width="1" transform="rotate(45)"/>
      <polygon points="0,12 -12,-8 12,-8" fill="rgba(185,28,28,0.4)" stroke="#991B1B" stroke-width="1.2"/>
      <circle cx="0" cy="0" r="2.5" fill="#D4A855"/>
    </g>
  </svg>`,
  Svadhisthana: `<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="40" fill="rgba(255,255,255,0.5)" stroke="#C2410C" stroke-width="0.5"/>
    <g transform="translate(50,50)">
      <g fill="rgba(234,88,12,0.3)" stroke="#9A3412" stroke-width="1">
        <path d="M 0,-36 Q -8,-28 -6,-20 L 6,-20 Q 8,-28 0,-36 Z"/>
        <path d="M 0,-36 Q -8,-28 -6,-20 L 6,-20 Q 8,-28 0,-36 Z" transform="rotate(60)"/>
        <path d="M 0,-36 Q -8,-28 -6,-20 L 6,-20 Q 8,-28 0,-36 Z" transform="rotate(120)"/>
        <path d="M 0,-36 Q -8,-28 -6,-20 L 6,-20 Q 8,-28 0,-36 Z" transform="rotate(180)"/>
        <path d="M 0,-36 Q -8,-28 -6,-20 L 6,-20 Q 8,-28 0,-36 Z" transform="rotate(240)"/>
        <path d="M 0,-36 Q -8,-28 -6,-20 L 6,-20 Q 8,-28 0,-36 Z" transform="rotate(300)"/>
      </g>
      <circle cx="0" cy="0" r="16" fill="rgba(255,255,255,0.85)" stroke="#9A3412" stroke-width="1"/>
      <path d="M -10,-2 A 10,10 0 1,0 -10,2 A 6,8 0 1,1 -10,-2 Z" fill="rgba(255,255,255,0.95)" stroke="#9A3412" stroke-width="1.2"/>
      <circle cx="0" cy="0" r="2.5" fill="#D4A855"/>
    </g>
  </svg>`,
  Manipura: `<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="40" fill="rgba(255,255,255,0.5)" stroke="#A16207" stroke-width="0.5"/>
    <g transform="translate(50,50)">
      <g fill="rgba(245,200,66,0.45)" stroke="#A16207" stroke-width="1">
        <path d="M 0,-36 Q -6,-28 -4,-20 L 4,-20 Q 6,-28 0,-36 Z"/>
        <path d="M 0,-36 Q -6,-28 -4,-20 L 4,-20 Q 6,-28 0,-36 Z" transform="rotate(36)"/>
        <path d="M 0,-36 Q -6,-28 -4,-20 L 4,-20 Q 6,-28 0,-36 Z" transform="rotate(72)"/>
        <path d="M 0,-36 Q -6,-28 -4,-20 L 4,-20 Q 6,-28 0,-36 Z" transform="rotate(108)"/>
        <path d="M 0,-36 Q -6,-28 -4,-20 L 4,-20 Q 6,-28 0,-36 Z" transform="rotate(144)"/>
        <path d="M 0,-36 Q -6,-28 -4,-20 L 4,-20 Q 6,-28 0,-36 Z" transform="rotate(180)"/>
        <path d="M 0,-36 Q -6,-28 -4,-20 L 4,-20 Q 6,-28 0,-36 Z" transform="rotate(216)"/>
        <path d="M 0,-36 Q -6,-28 -4,-20 L 4,-20 Q 6,-28 0,-36 Z" transform="rotate(252)"/>
        <path d="M 0,-36 Q -6,-28 -4,-20 L 4,-20 Q 6,-28 0,-36 Z" transform="rotate(288)"/>
        <path d="M 0,-36 Q -6,-28 -4,-20 L 4,-20 Q 6,-28 0,-36 Z" transform="rotate(324)"/>
      </g>
      <polygon points="0,14 -14,-9 14,-9" fill="rgba(220,38,38,0.55)" stroke="#991B1B" stroke-width="1.2"/>
      <circle cx="0" cy="0" r="2.5" fill="#D4A855"/>
    </g>
  </svg>`,
  Anahata: `<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="40" fill="rgba(255,255,255,0.5)" stroke="#15803D" stroke-width="0.5"/>
    <g transform="translate(50,50)">
      <g fill="rgba(34,197,94,0.35)" stroke="#15803D" stroke-width="1">
        <path d="M 0,-36 Q -5,-28 -4,-20 L 4,-20 Q 5,-28 0,-36 Z"/>
        <path d="M 0,-36 Q -5,-28 -4,-20 L 4,-20 Q 5,-28 0,-36 Z" transform="rotate(30)"/>
        <path d="M 0,-36 Q -5,-28 -4,-20 L 4,-20 Q 5,-28 0,-36 Z" transform="rotate(60)"/>
        <path d="M 0,-36 Q -5,-28 -4,-20 L 4,-20 Q 5,-28 0,-36 Z" transform="rotate(90)"/>
        <path d="M 0,-36 Q -5,-28 -4,-20 L 4,-20 Q 5,-28 0,-36 Z" transform="rotate(120)"/>
        <path d="M 0,-36 Q -5,-28 -4,-20 L 4,-20 Q 5,-28 0,-36 Z" transform="rotate(150)"/>
        <path d="M 0,-36 Q -5,-28 -4,-20 L 4,-20 Q 5,-28 0,-36 Z" transform="rotate(180)"/>
        <path d="M 0,-36 Q -5,-28 -4,-20 L 4,-20 Q 5,-28 0,-36 Z" transform="rotate(210)"/>
        <path d="M 0,-36 Q -5,-28 -4,-20 L 4,-20 Q 5,-28 0,-36 Z" transform="rotate(240)"/>
        <path d="M 0,-36 Q -5,-28 -4,-20 L 4,-20 Q 5,-28 0,-36 Z" transform="rotate(270)"/>
        <path d="M 0,-36 Q -5,-28 -4,-20 L 4,-20 Q 5,-28 0,-36 Z" transform="rotate(300)"/>
        <path d="M 0,-36 Q -5,-28 -4,-20 L 4,-20 Q 5,-28 0,-36 Z" transform="rotate(330)"/>
      </g>
      <polygon points="0,-14 -12,7 12,7" fill="none" stroke="#15803D" stroke-width="1.4"/>
      <polygon points="0,14 -12,-7 12,-7" fill="none" stroke="#15803D" stroke-width="1.4"/>
      <circle cx="0" cy="0" r="2.5" fill="#D4A855"/>
    </g>
  </svg>`,
  Vishuddha: `<svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="42" fill="rgba(255,255,255,0.5)" stroke="#1E40AF" stroke-width="0.5"/>
    <g transform="translate(50,50)">
      <g fill="rgba(30,64,175,0.25)" stroke="#1E3A8A" stroke-width="0.9">
        <path d="M 0,-38 Q -5,-30 -3.5,-22 L 3.5,-22 Q 5,-30 0,-38 Z"/>
        <path d="M 0,-38 Q -5,-30 -3.5,-22 L 3.5,-22 Q 5,-30 0,-38 Z" transform="rotate(22.5)"/>
        <path d="M 0,-38 Q -5,-30 -3.5,-22 L 3.5,-22 Q 5,-30 0,-38 Z" transform="rotate(45)"/>
        <path d="M 0,-38 Q -5,-30 -3.5,-22 L 3.5,-22 Q 5,-30 0,-38 Z" transform="rotate(67.5)"/>
        <path d="M 0,-38 Q -5,-30 -3.5,-22 L 3.5,-22 Q 5,-30 0,-38 Z" transform="rotate(90)"/>
        <path d="M 0,-38 Q -5,-30 -3.5,-22 L 3.5,-22 Q 5,-30 0,-38 Z" transform="rotate(112.5)"/>
        <path d="M 0,-38 Q -5,-30 -3.5,-22 L 3.5,-22 Q 5,-30 0,-38 Z" transform="rotate(135)"/>
        <path d="M 0,-38 Q -5,-30 -3.5,-22 L 3.5,-22 Q 5,-30 0,-38 Z" transform="rotate(157.5)"/>
        <path d="M 0,-38 Q -5,-30 -3.5,-22 L 3.5,-22 Q 5,-30 0,-38 Z" transform="rotate(180)"/>
        <path d="M 0,-38 Q -5,-30 -3.5,-22 L 3.5,-22 Q 5,-30 0,-38 Z" transform="rotate(202.5)"/>
        <path d="M 0,-38 Q -5,-30 -3.5,-22 L 3.5,-22 Q 5,-30 0,-38 Z" transform="rotate(225)"/>
        <path d="M 0,-38 Q -5,-30 -3.5,-22 L 3.5,-22 Q 5,-30 0,-38 Z" transform="rotate(247.5)"/>
        <path d="M 0,-38 Q -5,-30 -3.5,-22 L 3.5,-22 Q 5,-30 0,-38 Z" transform="rotate(270)"/>
        <path d="M 0,-38 Q -5,-30 -3.5,-22 L 3.5,-22 Q 5,-30 0,-38 Z" transform="rotate(292.5)"/>
        <path d="M 0,-38 Q -5,-30 -3.5,-22 L 3.5,-22 Q 5,-30 0,-38 Z" transform="rotate(315)"/>
        <path d="M 0,-38 Q -5,-30 -3.5,-22 L 3.5,-22 Q 5,-30 0,-38 Z" transform="rotate(337.5)"/>
      </g>
      <circle cx="0" cy="0" r="20" fill="rgba(255,255,255,0.85)" stroke="#1E40AF" stroke-width="1"/>
      <polygon points="0,16 -16,-10 16,-10" fill="rgba(255,255,255,0.85)" stroke="#1E40AF" stroke-width="1.2"/>
      <circle cx="0" cy="-2" r="6" fill="rgba(212,168,85,0.15)" stroke="#1E40AF" stroke-width="0.6"/>
      <path d="M -4,-2 Q 0,-5 4,-2 Q 0,1 -4,-2 Z" fill="rgba(255,255,255,0.7)" stroke="#1E40AF" stroke-width="0.4"/>
      <circle cx="0" cy="0" r="2.5" fill="#D4A855"/>
    </g>
  </svg>`,
};

// Brand lotus + footer emblem SVG strings
export const BRAND_LOTUS_SVG = `<svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
  <g transform="translate(24,24)">
    <ellipse cx="0" cy="-12" rx="4" ry="11" fill="rgba(212,168,85,0.25)" stroke="#D4A855" stroke-width="0.8"/>
    <ellipse cx="0" cy="-12" rx="4" ry="11" transform="rotate(45)" fill="rgba(212,168,85,0.25)" stroke="#D4A855" stroke-width="0.8"/>
    <ellipse cx="0" cy="-12" rx="4" ry="11" transform="rotate(90)" fill="rgba(212,168,85,0.25)" stroke="#D4A855" stroke-width="0.8"/>
    <ellipse cx="0" cy="-12" rx="4" ry="11" transform="rotate(135)" fill="rgba(212,168,85,0.25)" stroke="#D4A855" stroke-width="0.8"/>
    <ellipse cx="0" cy="-12" rx="4" ry="11" transform="rotate(180)" fill="rgba(212,168,85,0.25)" stroke="#D4A855" stroke-width="0.8"/>
    <ellipse cx="0" cy="-12" rx="4" ry="11" transform="rotate(225)" fill="rgba(212,168,85,0.25)" stroke="#D4A855" stroke-width="0.8"/>
    <ellipse cx="0" cy="-12" rx="4" ry="11" transform="rotate(270)" fill="rgba(212,168,85,0.25)" stroke="#D4A855" stroke-width="0.8"/>
    <ellipse cx="0" cy="-12" rx="4" ry="11" transform="rotate(315)" fill="rgba(212,168,85,0.25)" stroke="#D4A855" stroke-width="0.8"/>
    <circle cx="0" cy="0" r="4" fill="#D4A855"/>
    <circle cx="0" cy="0" r="2" fill="#FFE9B0"/>
  </g>
</svg>`;

export const FOOTER_EMBLEM_SVG = `<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
  <circle cx="10" cy="10" r="3.5" fill="#D4A855"/>
  <circle cx="10" cy="3.5" r="2" fill="none" stroke="#D4A855" stroke-width="0.6"/>
  <circle cx="10" cy="16.5" r="2" fill="none" stroke="#D4A855" stroke-width="0.6"/>
  <circle cx="3.5" cy="10" r="2" fill="none" stroke="#D4A855" stroke-width="0.6"/>
  <circle cx="16.5" cy="10" r="2" fill="none" stroke="#D4A855" stroke-width="0.6"/>
</svg>`;

export const AFFIRMATION_LOTUS_SVG = `<svg width="42" height="42" viewBox="0 0 42 42" xmlns="http://www.w3.org/2000/svg">
  <path d="M21 6 Q15 12 14 18 Q14 24 21 28 Q28 24 28 18 Q27 12 21 6Z" fill="rgba(212,168,85,0.4)" stroke="#D4A855" stroke-width="0.8"/>
  <ellipse cx="21" cy="18" rx="4" ry="2" fill="rgba(255,255,255,0.6)"/>
  <path d="M14 22 Q9 27 7 33 M28 22 Q33 27 35 33" stroke="rgba(212,168,85,0.4)" stroke-width="0.8" fill="none"/>
  <circle cx="21" cy="18" r="1" fill="#FFE9B0"/>
</svg>`;
